import Image from "next/image";
import {
    ArrowRight,
    Check,
    Cloud,
    Database,
    FileCheck2,
    LockKeyhole,
    Mic2,
    Share2,
    Sparkles,
    Users,
} from "lucide-react";

const DEFAULT_IMAGES = [
    "/images/chronelle/dashboard-clean-v3.png",
    "/images/chronelle/recording-clean-v3.png",
    "/images/chronelle/review-clean-v3.png",
];

const ADDITIONAL_SHOWCASE_IMAGES = [
    "/images/chronelle/resident-profile-v1.png",
    "/images/chronelle/prompt-library-v1.png",
    "/images/chronelle/family-share-v1.png",
];

export interface ChronelleHeroBlockProps {
    eyebrow?: string;
    title?: string;
    tagline?: string;
    description?: string;
    images?: string[];
    metadata?: Array<{ label: string; value: string }>;
}

export function ChronelleHeroBlock({
    eyebrow = "// Case Study",
    title = "Chronelle",
    tagline = "Preserving lifetimes, building communities.",
    description = "A tablet-first storytelling platform that helps care homes turn resident voices into structured, family-ready memories—without losing consent, context or human review.",
    images = DEFAULT_IMAGES,
    metadata = [
        { label: "Client / Product", value: "Chronelle / B2B SaaS" },
        { label: "Sector", value: "Adult Social Care" },
        { label: "Scope", value: "Product Strategy, UX/UI, AI Workflow" },
        { label: "Stage", value: "MVP & Pilot Validation" },
    ],
}: ChronelleHeroBlockProps) {
    return (
        <section className="relative min-h-[92vh] overflow-hidden bg-[#18211f] px-6 pb-20 pt-32 text-white md:px-12 lg:px-24 lg:pt-40">
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_76%_34%,rgba(85,191,208,0.14),transparent_28%),radial-gradient(circle_at_16%_86%,rgba(145,166,137,0.13),transparent_34%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:linear-gradient(to_bottom,black,transparent_88%)]" />
                <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#55bfd0]/35 to-transparent" />
            </div>

            <div className="relative z-10 mx-auto grid max-w-[1500px] grid-cols-1 items-center gap-16 lg:grid-cols-12">
                <div className="lg:col-span-6">
                    <p className="mb-8 font-mono text-xs uppercase tracking-[0.28em] text-[#55bfd0]">{eyebrow}</p>
                    <h1 className="text-[18vw] font-semibold leading-[0.78] tracking-[-0.075em] sm:text-8xl lg:text-[9rem]">{title}</h1>
                    <p className="mt-10 max-w-xl font-serif text-3xl leading-tight text-[#d9e6df] md:text-4xl">{tagline}</p>
                    <p className="mt-6 max-w-xl text-sm leading-7 text-white/60 md:text-base">{description}</p>

                    <div className="mt-12 grid max-w-2xl grid-cols-2 gap-x-8 gap-y-7 border-t border-white/15 pt-8">
                        {metadata.map((item) => (
                            <div key={item.label}>
                                <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/35">{item.label}</p>
                                <p className="mt-2 text-xs leading-5 text-white/80 sm:text-sm">{item.value}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative flex min-h-[510px] items-center justify-center lg:col-span-6 lg:min-h-[650px]">
                    <div className="absolute h-[420px] w-[420px] rounded-full bg-[#168ca0]/20 blur-[90px]" />
                    <div className="relative flex w-full items-end justify-center">
                        <div className="relative z-10 w-[31%] max-w-[230px] -rotate-[6deg] translate-x-5 translate-y-8">
                            <Image src={images[0] || DEFAULT_IMAGES[0]} alt="Chronelle care-home dashboard" width={890} height={1768} className="h-auto w-full drop-shadow-[0_30px_35px_rgba(0,0,0,0.45)]" style={{ clipPath: "inset(0.5% 0.8% round 9% / 4.5%)" }} priority />
                        </div>
                        <div className="relative z-20 w-[34%] max-w-[250px] -translate-y-5">
                            <Image src={images[1] || DEFAULT_IMAGES[1]} alt="Chronelle guided story recording" width={897} height={1753} className="h-auto w-full drop-shadow-[0_34px_40px_rgba(0,0,0,0.55)]" style={{ clipPath: "inset(0.5% 0.8% round 9% / 4.5%)" }} priority />
                        </div>
                        <div className="relative z-10 w-[31%] max-w-[230px] rotate-[6deg] -translate-x-5 translate-y-8">
                            <Image src={images[2] || DEFAULT_IMAGES[2]} alt="Chronelle staff review workflow" width={890} height={1767} className="h-auto w-full drop-shadow-[0_30px_35px_rgba(0,0,0,0.45)]" style={{ clipPath: "inset(0.5% 0.8% round 9% / 4.5%)" }} priority />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export interface ChronelleChallengeBlockProps {
    eyebrow?: string;
    title?: string;
    paragraphs?: string[];
    metrics?: Array<{ value: string; label: string }>;
}

export function ChronelleChallengeBlock({
    eyebrow = "01 / The Challenge",
    title = "When care becomes routine, identity can disappear.",
    paragraphs = [
        "Rich memories, skills and life stories are often reduced to brief care-plan notes. Staff want to listen, families want to preserve a loved one's voice, and residents want to feel known—but there is rarely a simple process for capturing those stories.",
        "Raw phone recordings do not solve the problem. Long, unstructured audio is difficult to revisit and easy to forget. The real design challenge was conversion: turn natural speech into clear, meaningful memories while keeping the resident's words, consent and dignity intact.",
    ],
    metrics = [
        { value: "940k", label: "Older people in the UK often experience loneliness" },
        { value: "360k+", label: "Care-home residents in England" },
        { value: "CQC", label: "Care guidance expects personal history and aspirations to be evidenced" },
    ],
}: ChronelleChallengeBlockProps) {
    return (
        <section className="bg-[#f5f1e9] px-6 py-24 text-[#202826] md:px-12 md:py-32 lg:px-24">
            <div className="mx-auto max-w-[1400px]">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#008ca4]">{eyebrow}</p>
                <div className="mt-8 grid gap-12 lg:grid-cols-12 lg:gap-20">
                    <h2 className="font-serif text-5xl leading-[0.98] tracking-[-0.035em] md:text-7xl lg:col-span-7">{title}</h2>
                    <div className="space-y-6 text-base leading-8 text-[#59615e] lg:col-span-5">
                        {paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                    </div>
                </div>

                <div className="mt-20 grid border-y border-[#202826]/15 md:grid-cols-3">
                    {metrics.map((metric, index) => (
                        <div key={metric.value} className={`py-8 md:px-8 ${index > 0 ? "border-t border-[#202826]/15 md:border-l md:border-t-0" : ""}`}>
                            <p className="text-4xl font-semibold tracking-tight text-[#008ca4] md:text-5xl">{metric.value}</p>
                            <p className="mt-3 max-w-xs text-sm leading-6 text-[#59615e]">{metric.label}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export interface ChronelleWorkflowBlockProps {
    eyebrow?: string;
    title?: string;
    description?: string;
    steps?: Array<{ title: string; description: string }>;
}

const WORKFLOW_ICONS = [Mic2, Sparkles, FileCheck2, Share2];

export function ChronelleWorkflowBlock({
    eyebrow = "02 / The Product",
    title = "One calm workflow from spoken memory to shared story.",
    description = "The MVP is deliberately narrow: help a resident speak, help staff shape and verify the story, then share it securely with family.",
    steps = [
        { title: "Record", description: "A large tap-to-talk interface and guided prompts reduce navigation and dexterity demands." },
        { title: "Curate", description: "Speech-to-text and AI-assisted formatting turn raw audio into readable story formats without inventing facts." },
        { title: "Approve", description: "Staff review every transcript, confirm consent and edit details before anything leaves the care home." },
        { title: "Share", description: "Approved stories become secure family links, audio keepsakes or printable pages." },
    ],
}: ChronelleWorkflowBlockProps) {
    return (
        <section className="bg-[#192321] px-6 py-24 text-white md:px-12 md:py-32 lg:px-24">
            <div className="mx-auto max-w-[1400px]">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#55bfd0]">{eyebrow}</p>
                <div className="mt-8 grid gap-10 lg:grid-cols-12">
                    <h2 className="font-serif text-5xl leading-none md:text-7xl lg:col-span-7">{title}</h2>
                    <p className="max-w-xl text-base leading-8 text-white/55 lg:col-span-5">{description}</p>
                </div>

                <div className="mt-20 grid gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-2 xl:grid-cols-4">
                    {steps.map((step, index) => {
                        const Icon = WORKFLOW_ICONS[index] || Check;
                        return (
                            <article key={step.title} className="min-h-[320px] bg-[#192321] p-8 transition-colors hover:bg-[#202d2a]">
                                <div className="flex items-center justify-between">
                                    <Icon className="text-[#55bfd0]" size={27} strokeWidth={1.5} />
                                    <span className="font-mono text-[10px] text-white/25">0{index + 1}</span>
                                </div>
                                <h3 className="mt-20 text-3xl font-medium">{step.title}</h3>
                                <p className="mt-5 text-sm leading-7 text-white/50">{step.description}</p>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export interface ChronelleShowcaseBlockProps {
    eyebrow?: string;
    title?: string;
    description?: string;
    images?: string[];
}

export function ChronelleShowcaseBlock({
    eyebrow = "03 / Interface System",
    title = "Designed for confidence, not digital fluency.",
    description = "Large targets, restrained navigation, familiar language and calm visual hierarchy make the experience usable for residents while giving staff the operational controls they need.",
    images = DEFAULT_IMAGES,
}: ChronelleShowcaseBlockProps) {
    const showcaseImages = [...images.slice(0, 3), ...ADDITIONAL_SHOWCASE_IMAGES];
    const captions = [
        "Care-home dashboard",
        "Resident recording flow",
        "Staff review and approval",
        "Resident profile and stories",
        "Guided prompt library",
        "Secure family sharing",
    ];
    return (
        <section className="overflow-hidden bg-[#edf3f1] px-6 py-24 text-[#202826] md:px-12 md:py-32 lg:px-24 lg:pb-44">
            <div className="mx-auto max-w-[1400px]">
                <div className="grid gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#008ca4]">{eyebrow}</p>
                        <h2 className="mt-8 font-serif text-5xl leading-none md:text-7xl">{title}</h2>
                    </div>
                    <p className="self-end text-base leading-8 text-[#59615e] lg:col-span-5">{description}</p>
                </div>

                <div className="mt-20 grid gap-x-8 gap-y-20 md:grid-cols-2 xl:grid-cols-3">
                    {showcaseImages.map((src, index) => (
                        <figure key={src} className={`${index % 3 === 1 ? "xl:translate-y-16" : index % 3 === 2 ? "xl:translate-y-32" : ""}`}>
                            <div className="relative mx-auto aspect-[359/712] w-full max-w-[350px]">
                                <Image src={src} alt={captions[index]} fill sizes="(max-width: 768px) 82vw, (max-width: 1280px) 42vw, 28vw" className="object-contain drop-shadow-[0_28px_35px_rgba(31,52,46,0.18)]" />
                            </div>
                            <figcaption className="mx-auto mt-6 flex max-w-[350px] items-center justify-between border-t border-[#202826]/15 pt-4 text-xs uppercase tracking-[0.16em] text-[#59615e]">
                                <span>{captions[index]}</span><span>0{index + 1}</span>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
}

export interface ChronelleCollaborationBlockProps {
    eyebrow?: string;
    title?: string;
    description?: string;
    principles?: Array<{ title: string; description: string }>;
}

export function ChronelleCollaborationBlock({
    eyebrow = "04 / The Collaboration",
    title = "Lived care experience became a focused product system.",
    description = "Kitebe worked with Chronelle to translate founder-led insight from care-home and NHS environments into a pilot-ready product direction. The collaboration connected the emotional need—helping people feel known—with the operational reality of consent, staff time and regulated care.",
    principles = [
        { title: "Accessibility first", description: "Every core action is designed around large targets, low cognitive load and staff-facilitated use." },
        { title: "Human-reviewed AI", description: "Automation structures resident speech; it never replaces staff judgement or approval." },
        { title: "Privacy as product", description: "Consent, role-based access, retention and deletion are part of the experience—not policy added later." },
        { title: "Operational fit", description: "The product supports repeatable 10–15 minute activity sessions rather than creating another complex workflow." },
    ],
}: ChronelleCollaborationBlockProps) {
    return (
        <section className="bg-[#f5f1e9] px-6 py-24 text-[#202826] md:px-12 md:py-32 lg:px-24">
            <div className="mx-auto grid max-w-[1400px] gap-16 lg:grid-cols-12 lg:gap-24">
                <div className="lg:col-span-6">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#008ca4]">{eyebrow}</p>
                    <h2 className="mt-8 font-serif text-5xl leading-[1.02] md:text-7xl">{title}</h2>
                    <p className="mt-8 max-w-2xl text-base leading-8 text-[#59615e]">{description}</p>
                </div>
                <div className="lg:col-span-6">
                    {principles.map((principle, index) => (
                        <article key={principle.title} className="grid grid-cols-[48px_1fr] gap-5 border-t border-[#202826]/15 py-7">
                            <span className="font-mono text-xs text-[#008ca4]">0{index + 1}</span>
                            <div>
                                <h3 className="text-xl font-medium">{principle.title}</h3>
                                <p className="mt-3 text-sm leading-7 text-[#59615e]">{principle.description}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

export interface ChronelleArchitectureBlockProps {
    eyebrow?: string;
    title?: string;
    description?: string;
}

const ARCHITECTURE_ITEMS = [
    { icon: Users, title: "Tablet experience", text: "Next.js interface optimised for iPad and Android tablets, with high contrast and large touch targets." },
    { icon: Cloud, title: "Serverless workflow", text: "Authentication, recording sessions, prompt selection, story generation and sharing links in one lean service layer." },
    { icon: Database, title: "Separated data", text: "Audio, transcripts, approved stories and consent records remain independently governed and auditable." },
    { icon: LockKeyhole, title: "UK/EU governance", text: "Encrypted storage, role-based access, audit logs, retention rules and a clear deletion path." },
];

export function ChronelleArchitectureBlock({
    eyebrow = "05 / Technology & Trust",
    title = "A lean architecture with care-grade safeguards.",
    description = "The MVP balances fast validation with the trust requirements of voice, personal history and family sharing. AI assistance remains transcript-grounded and every output stays behind mandatory staff review.",
}: ChronelleArchitectureBlockProps) {
    return (
        <section className="bg-[#17201f] px-6 py-24 text-white md:px-12 md:py-32 lg:px-24">
            <div className="mx-auto max-w-[1400px]">
                <div className="grid gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#55bfd0]">{eyebrow}</p>
                        <h2 className="mt-8 font-serif text-5xl leading-none md:text-7xl">{title}</h2>
                    </div>
                    <p className="self-end text-base leading-8 text-white/55 lg:col-span-5">{description}</p>
                </div>

                <div className="mt-20 grid gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-2">
                    {ARCHITECTURE_ITEMS.map(({ icon: Icon, title: itemTitle, text }) => (
                        <article key={itemTitle} className="bg-[#17201f] p-8 md:p-10">
                            <Icon size={26} strokeWidth={1.5} className="text-[#55bfd0]" />
                            <h3 className="mt-10 text-2xl font-medium">{itemTitle}</h3>
                            <p className="mt-4 max-w-xl text-sm leading-7 text-white/50">{text}</p>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

export interface ChronelleRoadmapBlockProps {
    eyebrow?: string;
    title?: string;
    description?: string;
    phases?: Array<{ period: string; title: string; items: string[] }>;
    targets?: Array<{ value: string; label: string }>;
}

export function ChronelleRoadmapBlock({
    eyebrow = "06 / Validation Roadmap",
    title = "Prove the story-capture workflow before expanding the community.",
    description = "Chronelle is at MVP stage. These are validation targets from the product plan—not reported outcomes.",
    phases = [
        { period: "0–6 months", title: "MVP pilots", items: ["Two local care-home pilots", "Structured weekly recording sessions", "Measure story completion, staff time and family response"] },
        { period: "6–12 months", title: "Early revenue", items: ["Convert validated pilots", "Build formal case studies and testimonials", "Refine onboarding, privacy and reporting"] },
        { period: "Year 2+", title: "Partner-led growth", items: ["Expand into small regional groups", "Explore care-platform partnerships", "Test a staff-moderated Social Wall after compliance validation"] },
    ],
    targets = [
        { value: "2", label: "pilot care homes" },
        { value: "10–20", label: "curated resident stories" },
        { value: "10+", label: "families receiving stories" },
    ],
}: ChronelleRoadmapBlockProps) {
    return (
        <section className="bg-[#f5f1e9] px-6 py-24 text-[#202826] md:px-12 md:py-32 lg:px-24">
            <div className="mx-auto max-w-[1400px]">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#008ca4]">{eyebrow}</p>
                <div className="mt-8 grid gap-10 lg:grid-cols-12">
                    <h2 className="font-serif text-5xl leading-none md:text-7xl lg:col-span-8">{title}</h2>
                    <p className="self-end text-base leading-8 text-[#59615e] lg:col-span-4">{description}</p>
                </div>

                <div className="mt-20 grid gap-6 lg:grid-cols-3">
                    {phases.map((phase, index) => (
                        <article key={phase.title} className={`${index === 0 ? "bg-[#dce9e5]" : "border border-[#202826]/15"} min-h-[360px] p-8`}>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#008ca4]">{phase.period}</p>
                            <h3 className="mt-8 text-3xl font-medium">{phase.title}</h3>
                            <ul className="mt-8 space-y-4">
                                {phase.items.map((item) => (
                                    <li key={item} className="flex gap-3 text-sm leading-6 text-[#59615e]"><Check size={16} className="mt-1 shrink-0 text-[#008ca4]" />{item}</li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>

                <div className="mt-16 grid border-y border-[#202826]/15 md:grid-cols-3">
                    {targets.map((target, index) => (
                        <div key={target.label} className={`py-8 md:px-8 ${index > 0 ? "border-t border-[#202826]/15 md:border-l md:border-t-0" : ""}`}>
                            <p className="text-4xl font-semibold text-[#008ca4] md:text-5xl">{target.value}</p>
                            <p className="mt-2 text-sm text-[#59615e]">{target.label}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export interface ChronelleCtaBlockProps {
    eyebrow?: string;
    title?: string;
    description?: string;
    buttonText?: string;
    buttonHref?: string;
}

export function ChronelleCtaBlock({
    eyebrow = "Build with Kitebe",
    title = "Design technology that makes people feel more human.",
    description = "From founder insight to an accessible, pilot-ready product system, we help teams turn meaningful problems into focused digital products.",
    buttonText = "Start a collaboration",
    buttonHref = "/contact",
}: ChronelleCtaBlockProps) {
    return (
        <section className="relative overflow-hidden bg-[#edf3f1] px-6 py-24 text-[#202826] md:px-12 md:py-32 lg:px-24">
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_18%,rgba(0,140,164,0.12),transparent_28%),radial-gradient(circle_at_84%_82%,rgba(145,166,137,0.2),transparent_34%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(rgba(32,40,38,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(32,40,38,0.035)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(circle_at_center,black,transparent_82%)]" />
            </div>
            <div className="relative mx-auto max-w-[1200px] text-center">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#008ca4]">{eyebrow}</p>
                <h2 className="mx-auto mt-8 max-w-5xl font-serif text-5xl leading-none md:text-7xl lg:text-8xl">{title}</h2>
                <p className="mx-auto mt-8 max-w-2xl text-base leading-8 text-[#59615e]">{description}</p>
                <a href={buttonHref} className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#18211f] px-7 py-4 text-sm font-medium text-white transition-transform hover:scale-[1.03]">
                    {buttonText}<ArrowRight size={17} />
                </a>
            </div>
        </section>
    );
}
